/* tslint:disable:no-string-literal */
import * as _ from 'lodash-es';

import moment from 'moment';
import {Injectable} from '@angular/core';
import {Actions, ofType, createEffect} from '@ngrx/effects';
import {catchError, map, tap, switchMap, withLatestFrom} from 'rxjs/operators';

import {ProjectsService} from '@app/services/projects.service';
import {
  AddToSavedList,
  Delete,
  DeleteFailure,
  DeleteSuccess,
  DoAuditSuccess,
  ExtProfilsActionTypes,
  GetOrganizationListFailure,
  GetOrganizationListSuccess,
  GetStats,
  GetStatsFailure,
  GetStatsSuccess,
  NewSuccess,
  RefreshLegacyListFailure,
  RefreshLegacyListSuccess,
  LoadReducedList,
  LoadReducedListSuccess,

  RefreshList,
  RefreshListSuccess,

  RefreshSavedList,
  RefreshSavedListSuccess,
  SaveList,
  SaveListFailure,
  SaveListSuccess,
  SaveSavedListSuccess,
  SelectUuid,
  Update, LoadReducedListFailure, RefreshListAllreadyLoaded
} from './ext-profils.actions';

import {Store} from '@ngrx/store';
import {AppState} from '@store/app.states';

import {ExtProfil} from '@models/Projects/extProfils';
import {CommonServicesService} from '@app/services/common-services.service';
import {TranslateService} from '@ngx-translate/core';
import {RefreshProjectsListFailure} from '@store/actions/projects.actions';
import {EMPTY, of} from 'rxjs';

import {AuthService} from '@app/services/auth/auth.service';
import {
  getCurrentExtProfilsState,
  getCurrentExtProfilsStateForAudit
} from '@app/components/projects/ext-profils/store/ext-profils.selector';
import {ExtIntegrityService} from '@app/components/projects/ext-profils/store/ext-integrity.service';
import {ToastrService} from 'ngx-toastr';
import Swal from 'sweetalert2';
import {ExtProfilsService} from '@app/components/projects/ext-profils/store/ext-profils.service';

@Injectable()
export class ExtProfilsEffects {
  constructor(
    private actions: Actions,
    private store: Store<AppState>,
    private commonServices: CommonServicesService,
    private projectsService: ProjectsService,
    private integrityService: ExtIntegrityService,
    private translate: TranslateService,
    private authService: AuthService,
    private toastr: ToastrService,
    private extProfilsService: ExtProfilsService
  ) {
  }


  new = createEffect(() => this.actions.pipe(
    ofType(ExtProfilsActionTypes.New),
    map((payLoad) => {
      const newExt: ExtProfil = {
        state: {
          isNew: true,
          isDirty: true,
          isValid: true,
          isSelected: false
        },
        infos: {
          created: new Date(),
          updated: null,
          createdBy: this.authService.getUsername(),
          tags: [],
          favorite: false,
          description: ''
        },
        uuid: this.commonServices.getNewUuid(),
        id: null,
        keyName: this.commonServices.getSlug(this.translate.instant('Nouveau')),
        keyDef: 'Text',
        label: {},

        isArchived: false,
        isInternal: false,
        neverAskContributor: false,
        randomAssignement: false,
        dataType: 'string',

        organizationRestrictions: {
          flagAll: false,
          ids: []
        }
      };

      newExt.label[this.translate.defaultLang] = this.translate.instant('Nouveau');

      return new NewSuccess(newExt);
    })
  ));


  refreshExtProfilsList = createEffect(() => this.actions.pipe(
    ofType(ExtProfilsActionTypes.RefreshList),
    map((action: RefreshList) => action.payload),
    withLatestFrom(this.store.select(getCurrentExtProfilsState)),
    map((state) => state),
    switchMap(([payload, state]) => {

      let forceRefresh = payload.forceRefresh || state.extProfilsList.length === 0;

      if (!forceRefresh) {
        forceRefresh = state.partialList;
      }

      if (state.extProfilsList.length > 0 && !forceRefresh) {
        return of(new RefreshListAllreadyLoaded());
      } else {
        this.store.dispatch(new RefreshSavedList({forceRefresh: true}));
        // console.log('[ext-profils.effects] ExtProfilsActionTypes.RefreshList');
        return this.projectsService.getExtProfilsList().pipe(
          map((data) => {
              this.extProfilsService.mergeExtProfils(data.data.list);
              return new RefreshListSuccess({extProfilsList: data.data.list});
            }
          ),
          catchError((error) => of(new RefreshProjectsListFailure({error})))
        );

      }
    })
  ));

  //
  // Pour les besoin du dashboard, inutile de charger TOUS les exts, juste ceux demandés :)
  //

  LoadReducedList = createEffect(() => this.actions.pipe(
    ofType(ExtProfilsActionTypes.LoadReducedList),
    map((action: LoadReducedList) => action.payload),
    switchMap((payload) => {
      // console.log('[ext-profils.effects] ExtProfilsActionTypes.LoadReducedList');
      let extList = payload.extList;
      if (extList.length) {
        extList = Array.from(new Set([...extList, 'age_range']));
      }
      return this.projectsService.getExtProfilsList(extList, true)
        .pipe(
          map((data) => {
            // const newExtList = Array.from(new Set(['age_range', ...data?.data?.list || data.data.datas]));
            const newExtList = data?.data?.list || data.data.datas;
            this.extProfilsService.mergeExtProfils(newExtList);
            return new LoadReducedListSuccess({extProfilsList: newExtList});
          }),
          catchError(
            (error) => {
              Swal.fire({
                title: this.translate.instant('[LoadReducedList]'),
                text: error,
                icon: 'error'
              });
              return of(new LoadReducedListFailure({error}));
            }
          )
        );

    })
  ));

  refreshExtLegacy$ = createEffect(() =>
    this.actions.pipe(
      ofType(ExtProfilsActionTypes.RefreshLegacyList),
      switchMap((payload) => {
        return this.projectsService.getExtProfilsListLegacy().pipe(
          map((data) => new RefreshLegacyListSuccess({extProfilsList: data.data.list})),
          catchError((error) => of(new RefreshLegacyListFailure({error})))
        );
      })
    )
  );

  saveExtProfilsList$ = createEffect(() =>
    this.actions.pipe(
      ofType(ExtProfilsActionTypes.SaveList),
      map((action: SaveList) => action.payload),
      switchMap((payload) => {
        return this.projectsService.saveExtProfilList(payload.insertOrUpdateList).pipe(
          map((data) => new SaveListSuccess({uuids: data.data.uuids})),
          catchError((error) => of(new SaveListFailure({error})))
        );
      })
    )
  );

  //
  saveExtProfilsListSuccess$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ExtProfilsActionTypes.SaveListSuccess),
        map((action: SaveListSuccess) => action.payload),
        tap((action) => {
          // console.log('action', action.uuids.length);
          this.toastr.info(
            `${action.uuids.length} ${this.translate.instant(' ext-profil(s) enregistré(s).')}`,
            this.translate.instant('Enregistrement'),
            {
              timeOut: 2000,
              progressBar: true
            }
          );
        })
      ),
    {dispatch: false}
  );

  deleteExtProfil$ = createEffect(() =>
    this.actions.pipe(
      ofType(ExtProfilsActionTypes.Delete),
      map((action: Delete) => action.payload),
      switchMap((payload) => {
        return this.projectsService.deleteExtProfilList(payload.uuids).pipe(
          map((data) => new DeleteSuccess({deleted: data.data.uuids})),
          catchError((error) => of(new DeleteFailure({error})))
        );
      })
    )
  );

  // ---------------------------------------------------------------------------------------------
  //
  // Rafraîchissement volontaire (click) des stats
  //

  getStatsRefresh = createEffect(() => this.actions.pipe(
    ofType(ExtProfilsActionTypes.GetStats),
    map((action: GetStats) => action.payload),
    switchMap((payload) => {
      // console.log('Effect getStats');
      if (!payload.uuid) {
        return EMPTY;
      }
      return this.projectsService.getStats(payload.uuid).pipe(
        map((resp) => new GetStatsSuccess({stats: resp.data.stats})),
        catchError((error) => of(new GetStatsFailure({error})))
      );
    })
  ));
  //
  // Rafraîchissement des stats quand on clique sur un ext.
  //

  getStats = createEffect(() => this.actions.pipe(
    ofType(ExtProfilsActionTypes.SelectUuid),
    map((action: SelectUuid) => action.payload),
    withLatestFrom(this.store.select(getCurrentExtProfilsState)),
    map((state) => state),
    switchMap(([payload, state]) => {
      //
      //
      // console.log('I am in Effect, payloadUUID=', payload.uuid);
      //
      if (
        state.uxInteractions.selectionMode !== 'single' ||
        state.uxInteractions.selectedUuids.length !== 1
      ) {
        return EMPTY;
      }

      const currentExtProfil = _.filter(state.extProfilsList, {
        uuid: state.uxInteractions.selectedUuid
      })[0];

      //
      //
      if (currentExtProfil?.infos['stats']) {
        // console.log('Déjà chargé :)', state.infos['stats']);
        const nObj = [];
        nObj[currentExtProfil.keyName] = currentExtProfil.infos['stats'];
        return of(new GetStatsSuccess({stats: nObj}));
      } else {
        // console.log('**** state', state?.infos['stats'] || '*** Pas de stats :)');
        // console.log('Appel API for STAT', payload.uuid);
        return this.projectsService.getStats(payload.uuid).pipe(
          map((data) => new GetStatsSuccess({stats: data.data.stats})),
          catchError((error) => of(new GetStatsFailure({error})))
        );
      }
    })
  ));
  // ---------------------------------------------------------------------------------------------


  refreshSavedList$ = createEffect(() => this.actions.pipe(
    ofType(ExtProfilsActionTypes.RefreshSavedList),
    map((action: RefreshSavedList) => action.payload),
    withLatestFrom(this.store.select(getCurrentExtProfilsState)),
    map((state) => state),
    switchMap(([payload, state]) => {
      // console.log('>>>> refreshExtProfilsList ACTION');
      const forceRefresh = payload.forceRefresh || false;

      if (state.extProfilsList.length > 0 && !forceRefresh) {
        return EMPTY;
      } else {
        return this.projectsService.getSavedList().pipe(
          map((r) => new RefreshSavedListSuccess({list: r.data.list})),
          catchError((error) => of(new RefreshProjectsListFailure({error})))
        );
      }
    })
  ));


  saveSavedLid = createEffect(() => this.actions.pipe(
    ofType(ExtProfilsActionTypes.AddToSavedList, ExtProfilsActionTypes.RemoveFromSavedList),
    map((action: AddToSavedList) => action.payload),
    withLatestFrom(this.store.select(getCurrentExtProfilsState)),
    map((state) => state),
    switchMap(([payload, state]) => {
      return this.projectsService.saveSavedList(state.savedExtProfils).pipe(
        map((r) => new SaveSavedListSuccess()),
        catchError((error) => of(new RefreshProjectsListFailure({error})))
      );
    })
  ));

  //
  //
  //
  getOrganizationList$ = createEffect(() =>
    this.actions.pipe(
      ofType(ExtProfilsActionTypes.GetOrganizationList, ExtProfilsActionTypes.RefreshList),
      withLatestFrom(this.store.select(getCurrentExtProfilsState)),
      map((state) => state),
      switchMap(([payload, state]) => {
        if (state.organizationList.length > 0 || !this.authService.isLogged()) {
          return EMPTY;
        }
        return this.projectsService.getOrganizationList().pipe(
          map((data) => {
            // console.log(data.data.datas.rows);
            return new GetOrganizationListSuccess({list: data.data.datas.rows});
          }),
          catchError((error) => of(new GetOrganizationListFailure({error})))
        );
      })
    )
  );


  launchAudit$ = createEffect(() => this.actions.pipe(
    ofType(ExtProfilsActionTypes.Update, ExtProfilsActionTypes.RefreshList),
    map((action: Update) => action.payload),
    withLatestFrom(this.store.select(getCurrentExtProfilsStateForAudit)),
    map((state) => state),
    switchMap(([payload, state]) => {

      //
      // Juste toutes les x secondes...
      //
      if (
        state.ext.extProfilsList.length > 0 &&
        state.ext.uxInteractions.audit.lastDate &&
        moment(state.ext.uxInteractions.audit.lastDate).add('5', 'seconds') > moment()
      ) {
        return EMPTY;
      }

      return this.integrityService
        .checkIntegrity(state.ext, state.userInfo)
        .pipe(map((r) => new DoAuditSuccess({result: r})));
      //
    })
  ));
}
